import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaChartBar, FaCheckCircle, FaMoneyBillWave, FaSyncAlt, FaUsers } from 'react-icons/fa';
import AdminNav from '../components/admin/AdminNav';
import Button from '../components/common/Button';
import Section from '../components/common/Section';
import { fetchAdminDashboard } from '../services/registrationService';
import { clearAdminToken, getAdminToken } from '../utils/adminAuth';

const formatNumber = (value) => Number(value || 0).toLocaleString('en');
const formatMoney = (value) => `NLe ${Number(value || 0).toLocaleString('en')}`;

const AdminDashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadStats = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await fetchAdminDashboard(getAdminToken());
      setStats(data);
    } catch (requestError) {
      if (requestError.response?.status === 401) {
        clearAdminToken();
      }
      setError(requestError.response?.data?.message || 'Unable to load dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cards = [
    {
      label: 'Total Registrations',
      value: formatNumber(stats?.total_registrations),
      icon: <FaUsers />,
      helper: 'All submitted applications'
    },
    {
      label: 'Registration Fees Paid',
      value: formatNumber(stats?.paid_registrations),
      icon: <FaCheckCircle />,
      helper: `${formatNumber(stats?.unpaid_registrations)} unpaid`
    },
    {
      label: 'Students With Paid Courses',
      value: formatNumber(stats?.students_with_paid_courses),
      icon: <FaChartBar />,
      helper: 'At least one course fee paid'
    },
    {
      label: 'Total Paid Amount',
      value: formatMoney(stats?.total_paid_amount),
      icon: <FaMoneyBillWave />,
      helper: 'Processed or completed payments'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Afrovate Tech Hub</title>
      </Helmet>

      <section className="bg-primary-dark pt-32 pb-16 text-white">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Admin Dashboard</h1>
          <p className="text-white text-opacity-90 text-lg">Overview of registrations, payment status, and program activity.</p>
        </div>
      </section>

      <Section background="white" padding="lg">
        <AdminNav />

        <div className="flex justify-end mb-6">
          <Button variant="outline-primary" onClick={loadStats} disabled={loading} icon={<FaSyncAlt className={loading ? 'animate-spin' : ''} />}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
        {error && <p className="text-red-600 mb-6">{error}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {cards.map((card) => (
            <div key={card.label} className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-gray-600">{card.label}</p>
                <span className="text-primary text-2xl">{card.icon}</span>
              </div>
              <p className="text-3xl font-bold text-primary-dark">{card.value}</p>
              <p className="text-sm text-gray-500 mt-2">{card.helper}</p>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
};

export default AdminDashboardPage;
